import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNavigationContext } from '../context/NavigationContext';
import { prefetchRoute } from '../routePrefetch';

/**
 * Drop-in replacement for react-router-dom's <Link>.
 * Wraps navigation in React 18 startTransition so the old page
 * stays visible while the new lazy chunk downloads.
 * Also prefetches the target chunk on hover for near-instant navigation.
 *
 * Supports all standard Link props (to, className, children, etc.)
 * Falls back to normal browser navigation for:
 *   - Middle clicks / cmd+click / ctrl+click  (new tab)
 *   - External URLs
 *   - Links with target="_blank"
 */
const AppLink = React.forwardRef(({ to, onClick: userOnClick, children, ...props }, ref) => {
  const navigate = useNavigate();
  const { startTransition } = useNavigationContext();

  const handleClick = (e) => {
    // Run any user-provided onClick first
    if (userOnClick) userOnClick(e);
    if (e.defaultPrevented) return;

    // Let the browser handle modified clicks (new tab, etc.)
    if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;

    // Let browser handle target="_blank" / external links
    if (props.target === '_blank') return;

    // Prevent default Link navigation and do it inside startTransition
    e.preventDefault();
    startTransition(() => {
      navigate(to);
    });
  };

  const handleMouseEnter = () => {
    if (typeof to === 'string') {
      prefetchRoute(to);
    }
  };

  return (
    <Link ref={ref} to={to} onClick={handleClick} onMouseEnter={handleMouseEnter} {...props}>
      {children}
    </Link>
  );
});

AppLink.displayName = 'AppLink';

export default AppLink;
